/**
 * config/basemap.js
 * ─────────────────────────────────────────────────────────────────────────────
 * DASHBOARD BASEMAP REGISTRY — the single source of truth for the raster tile
 * layer behind the dashboard's remaining Leaflet map: the brand order route
 * preview in views/Forms/Listings/Listing/BrandVendorOrderDetails.js.
 *
 * The two rider tracking screens that also used to live here have since moved
 * to Google Maps (components/Maps/RiderFleetMap.js). This file stays because
 * the route preview renders inside a generated standalone HTML document, where
 * putting the Google key would mean shipping it in the printed page.
 *
 * WHY THIS FILE EXISTS
 *   CARTO changed the rules on its raster basemaps: requests to
 *   basemaps.cartocdn.com without an API key are now served tiles with an
 *   "API KEY REQUIRED" watermark baked into the image. Three separate screens
 *   had the same anonymous URL hardcoded, so they all broke at once and all
 *   would have needed the same edit. They now import from here instead.
 *
 * HOW IT BEHAVES
 *   • REACT_APP_CARTO_BASEMAP_KEY set  → CARTO Voyager, the look we shipped.
 *   • REACT_APP_CARTO_BASEMAP_KEY unset → standard OpenStreetMap tiles, which
 *     need no key. Different cartography, but an unwatermarked, working map
 *     rather than a broken-looking one. This is the safety net, not the target.
 *
 * INVARIANTS
 *   • No map screen may hardcode a tile URL. Import BASEMAP_TILE_PROPS (or
 *     BASEMAP_TILE_URL for raw Leaflet) and pass it through.
 *   • CRA bakes REACT_APP_* into the bundle at BUILD time, so on Heroku the
 *     config var must be set BEFORE the build step, not after:
 *       heroku config:set REACT_APP_CARTO_BASEMAP_KEY=<key> --app <your-app>
 *   • The key ships to the browser (every web map key does). Restrict it by
 *     referrer in the CARTO key console rather than treating it as a secret.
 *   • Swapping provider is an edit HERE and nowhere else.
 */

const CARTO_KEY = (process.env.REACT_APP_CARTO_BASEMAP_KEY || "").trim();

/** True when a CARTO key is configured for this build. */
export const HAS_CARTO_BASEMAP_KEY = CARTO_KEY.length > 0;

const CARTO_VOYAGER = {
  url: `https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`,
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
  subdomains: "abcd",
  maxZoom: 20,
};

const OPENSTREETMAP = {
  url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  attribution:
    '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  subdomains: "abc",
  maxZoom: 19,
};

/**
 * Everything a tile layer needs, in one object.
 * react-leaflet:  <TileLayer {...BASEMAP_TILE_PROPS} />
 * raw Leaflet:    L.tileLayer(BASEMAP_TILE_PROPS.url, BASEMAP_TILE_PROPS)
 */
export const BASEMAP_TILE_PROPS = HAS_CARTO_BASEMAP_KEY
  ? CARTO_VOYAGER
  : OPENSTREETMAP;

export const BASEMAP_TILE_URL = BASEMAP_TILE_PROPS.url;
export const BASEMAP_ATTRIBUTION = BASEMAP_TILE_PROPS.attribution;
export const BASEMAP_SUBDOMAINS = BASEMAP_TILE_PROPS.subdomains;
export const BASEMAP_MAX_ZOOM = BASEMAP_TILE_PROPS.maxZoom;

export default BASEMAP_TILE_PROPS;
